
import { useMemo } from 'react';

interface Order {
  id: string;
  product_price: number;
  commission_amount: number;
  payment_status: string;
  order_status: string;
  created_at: string;
}

interface Props {
  orders: Order[];
}

/**
 * StatsCards component renders a set of statistic cards based on the provided orders.
 * It safely handles edge‑cases such as empty order lists or malformed dates.
 */
export default function StatsCards({ orders }: Props) {
  // Guard against undefined / null orders (should never happen with proper typing,
  // but adds an extra layer of safety for runtime misuse).
  const safeOrders = Array.isArray(orders) ? orders : [];

  const stats = useMemo(() => {
    // Filter orders safely – ignore any entry that lacks the expected fields.
    const completed = safeOrders.filter(
      (o) => o?.payment_status === 'completed'
    );
    const processing = safeOrders.filter(
      (o) =>
        o?.payment_status === 'processing' || o?.payment_status === 'pending'
    );

    const totalRevenue = completed.reduce(
      (sum, o) => sum + Number(o?.product_price ?? 0),
      0
    );
    const totalCommission = completed.reduce(
      (sum, o) => sum + Number(o?.commission_amount ?? 0),
      0
    );
    const avgOrderValue =
      completed.length > 0 ? totalRevenue / completed.length : 0;

    // Revenue today – protect against invalid dates.
    const today = new Date().toDateString();
    const todayRevenue = completed
      .filter((o) => {
        const orderDate = new Date(o?.created_at ?? '');
        return !isNaN(orderDate.getTime())
          ? orderDate.toDateString() === today
          : false;
      })
      .reduce((sum, o) => sum + Number(o?.product_price ?? 0), 0);

    return {
      totalRevenue,
      totalCommission,
      avgOrderValue,
      completedCount: completed.length,
      processingCount: processing.length,
      todayRevenue,
    };
  }, [safeOrders]);

  const cards = [
    {
      label: 'Total Revenue',
      value: `$${stats.totalRevenue.toFixed(2)}`,
      sub: `${stats.completedCount} completed orders`,
      icon: 'ri-money-dollar-circle-line',
      iconColor: 'text-teal-500',
      bg: 'bg-teal-50',
      trend: null,
    },
    {
      label: 'Your Commission',
      value: `$${stats.totalCommission.toFixed(2)}`,
      sub: '15% avg rate',
      icon: 'ri-wallet-3-line',
      iconColor: 'text-emerald-500',
      bg: 'bg-emerald-50',
      trend: null,
    },
    {
      label: "Today's Revenue",
      value: `$${stats.todayRevenue.toFixed(2)}`,
      sub: 'Sales today',
      icon: 'ri-calendar-check-line',
      iconColor: 'text-orange-500',
      bg: 'bg-orange-50',
      trend: null,
    },
    {
      label: 'Avg Order Value',
      value: `$${stats.avgOrderValue.toFixed(2)}`,
      sub: 'Per transaction',
      icon: 'ri-line-chart-line',
      iconColor: 'text-rose-500',
      bg: 'bg-rose-50',
      trend: null,
    },
    {
      label: 'Processing',
      value: stats.processingCount.toString(),
      sub: 'Pending fulfillment',
      icon: 'ri-loader-3-line',
      iconColor: 'text-amber-500',
      bg: 'bg-amber-50',
      trend: null,
    },
    {
      label: 'Total Orders',
      value: safeOrders.length.toString(),
      sub: 'All time',
      icon: 'ri-shopping-bag-3-line',
      iconColor: 'text-indigo-500',
      bg: 'bg-indigo-50',
      trend: null,
    },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
      {cards.map((card) => (
        <div
          key={card.label}
          className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
        >
          <div className={`w-10 h-10 ${card.bg} rounded-lg flex items-center justify-center mb-3`}>
            <i className={`${card.icon} text-xl ${card.iconColor}`} />
          </div>
          <div className="text-2xl font-bold text-gray-900 mb-0.5">{card.value}</div>
          <div className="text-xs font-medium text-gray-500">{card.label}</div>
          <div className="text-xs text-gray-400 mt-1">{card.sub}</div>
        </div>
      ))}
    </div>
  );
}
